import * as React from 'react';
import { AlertBanner, Button } from '@ministry/ui/internal';

import { ApiError } from '../lib/api-client.js';
import { useT } from '../i18n/strings.js';

/**
 * SCREEN_SPECS.md I24's "500 System Error" case, shared: "Something went wrong on our
 * side. Please try again in a moment." — never a raw stack trace, database error, or
 * internal ID, but WITH the request's correlation ID in small print for support purposes
 * (traceable end-to-end per ARCHITECTURE.md's logging design) without exposing anything
 * sensitive. `ApiError.correlationId` (added this pass — see `lib/api-client.ts`) is what
 * makes this renderable at all; before that field existed, the ID was discarded the
 * moment a request failed and no screen in this app could have shown it.
 */
export interface ServerErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

export function ServerErrorState({ error, onRetry }: ServerErrorStateProps): React.JSX.Element {
  const t = useT();
  const correlationId = error instanceof ApiError ? error.correlationId : null;

  return (
    <AlertBanner
      variant="critical"
      title={t('serverError.title')}
      action={onRetry ? <Button size="sm" variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
    >
      <div className="flex flex-col gap-1">
        <span>{t('serverError.body')}</span>
        {correlationId ? <span className="text-xs text-text-muted">{t('serverError.correlationId', { id: correlationId })}</span> : null}
      </div>
    </AlertBanner>
  );
}
